import { useHistory, useLocation, useParams } from "react-router-dom";
import { Mode } from "../Locations/AddEditLocationForm";
import {
  PageHeader,
  useTreeData,
  confirmDialog,
  BaseButton,
  MenuButton,
} from "@velocity-ehs/component-library";
import {
  getStatusIndicatorProps,
  Status,
} from "../../sharedComponents/StatusIndicator";
import { useTranslation } from "react-i18next";
import VerticalTabs, { TabItem } from "../../sharedComponents/VerticalTabs";
import { useFlags } from "launchdarkly-react-client-sdk";
import { LocationProfileNew } from "./LocationProfileNew";
import LocationsManageApplications from "./LocationsManageApplications";
import { useGetLocation } from "../../../hooks/useGetLocation";
import useLocationMutation, {
  LocationActions,
} from "../../../hooks/useLocationMutation";
import {
  getCountryFromCode,
  getSubdivisionFromName,
} from "../Locations/Country";
import {
  LocationContextProps,
  useLocationsContext,
} from "../../../context/LocationsContext";
import LocationsRoleAssignment from "./LocationsRoleAssignment";
import UsersService from "@velocity-ehs/user-utils-module";
import * as Permissions from "../../supportFunctions/Permissions";
import {
  useGetCustomerApplicationBundles,
  useGetLocationApplicationBundles,
} from "../../../hooks/useGetApplicationBundles";
import { useApplicationBundleMutation } from "../../../hooks/useApplicationBundlesMutation";
import { useState } from "react";

interface Props {
  mode: Mode;
}
const ManageLocationNew: React.FC<Props> = ({ mode }) => {
  const treeData = useTreeData();
  const locationsContext: LocationContextProps = useLocationsContext();
  const [showModal, setShowModal] = useState<boolean>(false);
  const { t } = useTranslation();
  const { id } = useParams() as { id: string };
  const history = useHistory();

  const urlLocation = useLocation();
  const searchParams = new URLSearchParams(urlLocation.search);
  const fromDetails = searchParams.get("from");

  const { location, isFetching, refetch } = useGetLocation(id);

  const { productChangeTracking } = useFlags();

  const { mutations } = useLocationMutation();

  const isAdd = mode === Mode.Add;

  const getActionButtons = (): any[] => {
    if (!location) return [];
    const items = [
      {
        label: t("location_add_child_location"),
        show: location.viewerCanAddChildLocation,
        command: () => {
          return history.push(
            `/location/${location.id}/add?from=locations-details`
          );
        },
      },
      {
        label: t("universal_Activate"),
        command: () =>
          confirmDialog({
            message: t("locations_Change_Status_Active_Warning"),
            header: `${t("universal_Activate")} ${t(
              "update_location_status_modal_location"
            )}`,
            accept: () => changeLocationStatus("Active"),
            reject: () => {},
            acceptLabel: t("universal_Activate"),
            rejectLabel: t("universal_Cancel"),
          }),
        show:
          location.viewerCanActivateLocation &&
          location.locationStatus === "InActive",
      },
      {
        label: t("universal_Inactivate"),
        command: () =>
          confirmDialog({
            message: t("locations_Change_Status_Inactive_Warning"),
            header: `${t("universal_Inactivate")} ${t(
              "update_location_status_modal_location"
            )}`,
            accept: () => changeLocationStatus("Inactive"),
            reject: () => {},
            acceptLabel: t("universal_Inactivate"),
            rejectLabel: t("universal_Cancel"),
          }),
        show:
          (location.viewerCanInactivateLocation &&
            location.locationStatus === "Active") ||
          (location.viewerCanInactivateLocation &&
            location.locationStatus === "Archived"),
      },
      {
        label: t("universal_archive"),
        command: () =>
          confirmDialog({
            message: t("locations_Change_Status_Archived_Warning"),
            header: `${t("universal_archive")} ${t(
              "update_location_status_modal_location"
            )}`,
            accept: () => changeLocationStatus("Archived"),
            reject: () => {},
            acceptLabel: t("universal_archive"),
            rejectLabel: t("universal_Cancel"),
          }),
        show:
          location.viewerCanArchiveLocation &&
          location.locationStatus == "InActive",
      },
    ];
    return items.filter((item) => item.show);
  };

  const changeLocationStatus = async (status: Status) => {
    const countryValue = getCountryFromCode(
      location?.address?.nation || undefined,
      locationsContext.countryList
    );

    const subValue = getSubdivisionFromName(
      location?.address?.subdivision || undefined,
      countryValue?.subdivisions
    );

    const action =
      status === "Archived"
        ? LocationActions.ARCHIVE
        : location.locationStatus === "Active"
        ? LocationActions.INACTIVATE
        : LocationActions.UPDATE;

    await mutations.mutate(
      action,
      {
        locationStatus: status,
        isPhysicalLocation: location.isPhysicalLocation,
        address: {
          ...location.address,
          nation: countryValue,
          subdivision: subValue,
        },
      },
      location.id
    );
    treeData.invalidateAllQueries();
    refetch();
  };

  const {
    loading: isLocationApplicationBundles,
    applications: locationApplications,
    refetch: refetchLocationBundles,
  } = useGetLocationApplicationBundles(id);

  const {
    loading: isCustomerApplicationsLoading,
    applications: customerApplications,
  } = useGetCustomerApplicationBundles();

  const { mutations: applicationBundleMutations } =
    useApplicationBundleMutation();

  const onSubmit = () => {
    if (urlLocation.pathname.includes("/manage-applications")) {
      setShowModal(true);
    }
  };

  const onCloseModal = () => {
    setShowModal(false);
  };

  const isReadOnly =
    location?.locationStatus === "Inactive" ||
    location?.locationStatus === "InActive" ||
    location?.locationStatus === "Archived" ||
    !location?.viewerCanManageLocation;

  const getTabs = () => {
    const tabs: TabItem[] = [
      {
        title: t("universal_Details"),
        content: (
          <LocationProfileNew
            location={location}
            mode={mode}
            mutations={mutations.mutate}
            isLoading={isFetching}
            readOnly={isReadOnly}
          />
        ),
      },
    ];

    if (!isAdd) {
      tabs.push({
        path: "/manage-applications",
        title: t("locations_applications"),

        content: (
          <LocationsManageApplications
            isLoading={
              isCustomerApplicationsLoading ||
              isLocationApplicationBundles ||
              applicationBundleMutations.isLoading
            }
            locationApplications={locationApplications}
            customerApplications={customerApplications}
            mutate={applicationBundleMutations.mutate}
            showModal={showModal}
            closeModal={onCloseModal}
            readOnly={isReadOnly}
            refetchLocationBundles={refetchLocationBundles}
          />
        ),
      });
    }

    if (
      !isAdd &&
      Permissions.IsAllowed([Permissions.canViewRole], UsersService.permissions)
    ) {
      tabs.push({
        path: "/role-assigment",
        title: t("meta_role_assignments"),
        content: <LocationsRoleAssignment />,
      });
    }
    return tabs;
  };
  return (
    <>
      <PageHeader
        title={!isAdd ? location?.name : t("locations_Add_Location")}
        subtitle={location?.lineage}
        statusIndicator={
          !isAdd &&
          location &&
          getStatusIndicatorProps({
            status: location.locationStatus as Status,
          })
        }
        navigationTree={[
          { text: `Accelerate ${t("universal_Home")}`, href: "/" },
          { text: t("universal_Locations"), href: "/location" },
          { text: !isAdd ? t("action_edit") : t("universal_Add"), href: "" },
        ]}
        actionsTemplate={
          <>
            {!isAdd ? (
              <MenuButton
                label={t("universal_Actions")}
                id={"actions"}
                items={getActionButtons()}
                icon={"pi-chevron-down"}
                forceIcon
                buttonClass="p-button-text"
                disabled={getActionButtons().length === 0}
              />
            ) : null}
            <BaseButton
              label={t("universal_Cancel")}
              id="cancel"
              type="button"
              onClick={() =>
                history.push(
                  fromDetails !== null ? `/location/${id}/edit` : "/location"
                )
              }
              className="p-button-text"
            />
            <BaseButton
              label={t("universal_Save")}
              type="submit"
              id="locationsNewForm"
              form="locationsNewForm"
              loading={
                mutations.isLoading || applicationBundleMutations.isLoading
              }
              onClick={onSubmit}
              disabled={isReadOnly}
            />
          </>
        }
      />
      <VerticalTabs
        includeChangeTracking={productChangeTracking && !isAdd}
        targetIdentifierType="locations"
        tabs={getTabs()}
      />
    </>
  );
};

export default ManageLocationNew;
